import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleEvents,
  type HuddleEventsResult,
  getHuddleEventsOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleEventsToColumns,
  toHuddleEventsQueryDate,
  type HuddleEventsColumn,
} from '@utils/dashboardHuddle.util'

export type UseHuddleEventsOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleEventsResult = {
  data: HuddleEventsResult | null
  loading: boolean
  error: string | null
  columns: HuddleEventsColumn[]
}

function useHuddleEventsFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleEventsOptions & {
  fetcher: HuddleDateFetcher<HuddleEventsResult>
}): UseHuddleEventsResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load events',
    toQueryDate: toHuddleEventsQueryDate,
  })

  const columns = useMemo(() => mapHuddleEventsToColumns(data), [data])

  return { data, loading, error, columns }
}

export function useHuddleEvents(
  options: UseHuddleEventsOptions,
): UseHuddleEventsResult {
  return useHuddleEventsFromFetcher({ ...options, fetcher: getHuddleEvents })
}
export function useHuddleEventsOverview(
  options: UseHuddleEventsOptions,
): UseHuddleEventsResult {
  return useHuddleEventsFromFetcher({
    ...options,
    fetcher: getHuddleEventsOverview,
  })
}
